import { useEffect, useId, useRef, type ReactNode } from "react";
import { Button, Input, Segmented, Switch } from "@omnabase/ui";
import { FORM_BLOCK_ELEMENTS, FORM_LIMITS, FORM_MAPPING_LABELS, isFormBlock, type FormBlockElement, type FormMapping } from "@omnabase/shared";
import { connectionsFor, duplicable, removable, type DraftBlock, type DraftField, type DraftInput, type FieldPatch, type OpenProject } from "../../../lib/formEdits";

/** An open row's settings (builder v2, the founder's design; formerly round one's panel "Field settings"): label, placeholder (the typed
 *  fields), help text, a hidden field's value, a select's options or the project field's projects (listed explicitly, question 14), the
 *  Required with the key in small type beside it (its rule and uniqueness checked as typed), then Duplicate and Delete. The form editor round:
 *  first "Connects to" (what the field stands for: none or one of the details its type can hold, each once in a form; the address field keeps
 *  its own). The text blocks round: no Type menu (a field's type is chosen once, in "+ Field"); "Connects to" lists only the details the
 *  field's type can hold and is left out where there are none (checkbox, file); a Text block's row holds its words ("Text", a plain box of
 *  several lines) and its element (H1 to H6 or P), then Duplicate and Delete.
 *  The email field has no Delete (every form keeps it), the project field no Duplicate (finding 17). Readers see the same settings, disabled,
 *  without the two buttons. */
export type PanelEdits = {
  update: (patch: FieldPatch) => void; connect: (mapping: FormMapping | null) => void; optionLabel: (index: number, label: string) => void; addOption: () => void; removeOption: (index: number) => void;
  toggleProject: (p: OpenProject, on: boolean) => void;
};
export type FieldChecks = { keyProblem: string | null; /** round two: the key a published form saved this field with, when the draft changed it */ keyWas: string | null; fieldProblem: string | null };
const TYPED = new Set(["text", "textarea", "email", "select"]);
type Props = {
  field: DraftField; edit: boolean; checks: FieldChecks; projects: readonly OpenProject[]; edits: PanelEdits; /** 40 items: no copy */ full: boolean;
  /** whether this field may take a detail (not taken by another field, fitting its type) */ canConnect: (m: FormMapping) => boolean;
  /** an item just added: its first box takes the focus (again whenever the number changes) */ focusLabel: number | null; onDuplicate: () => void; onDelete: () => void;
};

export function FieldSettings(p: Props) {
  const root = useRef<HTMLDivElement>(null);
  const block = isFormBlock(p.field);
  useEffect(() => { if (p.focusLabel !== null) root.current?.querySelector<HTMLElement>(block ? "textarea" : "input")?.focus(); }, [p.focusLabel, block]); // a field's first box is the Label, a block's its Text
  return (
    <div className="ob-form-builder__settings" ref={root}>
      {isFormBlock(p.field) ? <BlockSettings block={p.field} edit={p.edit} edits={p.edits} /> : <InputSettings {...p} field={p.field} />}
      {p.checks.fieldProblem && <p className="ob-form-builder__problem" role="alert">{p.checks.fieldProblem}</p>}
      {!removable(p.field) && <p className="ob-form-builder__hint">Every form keeps its email field: it is how the team answers.</p>}
      <ItemTools {...p} />
    </div>
  );
}

/** a field's own settings, top to bottom */
function InputSettings({ field: f, edit, checks, projects, edits, canConnect }: Props & { field: DraftInput }) {
  return <>
    <ConnectsTo field={f} edit={edit} canConnect={canConnect} edits={edits} />
    <Setting label="Label">{(id) => <Input id={id} className="ob-input--text" value={f.label} maxLength={FORM_LIMITS.label} disabled={!edit} onChange={(e) => edits.update({ label: e.target.value })} />}</Setting>
    {TYPED.has(f.type) && <Setting label="Placeholder">{(id) => <Input id={id} className="ob-input--text" value={f.placeholder ?? ""} maxLength={FORM_LIMITS.placeholder} disabled={!edit} placeholder="Shown inside the empty field" onChange={(e) => edits.update({ placeholder: e.target.value })} />}</Setting>}
    <Setting label="Help text">{(id) => <Input id={id} className="ob-input--text" value={f.help ?? ""} maxLength={FORM_LIMITS.help} disabled={!edit} placeholder="A line under the field" onChange={(e) => edits.update({ help: e.target.value })} />}</Setting>
    {f.type === "hidden" && <Setting label="Value" hint="Sent with every submission; a page address can fill it too.">{(id) => <Input id={id} className="ob-input--text" value={f.hiddenValue ?? ""} maxLength={FORM_LIMITS.hiddenValue} disabled={!edit} onChange={(e) => edits.update({ hiddenValue: e.target.value })} />}</Setting>}
    {f.type === "select" && f.mapping !== "project" && <Options field={f} edit={edit} edits={edits} />}
    {f.mapping === "project" && <Projects field={f} edit={edit} projects={projects} edits={edits} />}
    <RequiredAndKey field={f} edit={edit} checks={checks} edits={edits} />
  </>;
}

const ELEMENTS = FORM_BLOCK_ELEMENTS.map((e) => ({ id: e, label: e.toUpperCase() }));
/** a Text block's settings: its words (plain, several lines, kept as typed) and the element it is drawn as */
function BlockSettings({ block: b, edit, edits }: { block: DraftBlock; edit: boolean; edits: PanelEdits }) {
  return <>
    <Setting label="Text">{(id) => <textarea id={id} className="ob-textarea ob-form-builder__block-text" rows={3} value={b.text} maxLength={FORM_LIMITS.block} disabled={!edit} placeholder="A heading or a paragraph" onChange={(e) => edits.update({ text: e.target.value })} />}</Setting>
    <div className="ob-form-builder__setting">
      <span className="ob-field-label">Shown as</span>
      <Segmented<FormBlockElement> label="Shown as" items={ELEMENTS.map((e) => ({ ...e, disabled: !edit }))} value={b.as} onChange={(as) => edits.update({ as })} />
    </div>
  </>;
}

/** Duplicate and Delete, for editors (none on a reader's view) */
function ItemTools({ field: f, edit, full, onDuplicate, onDelete }: Props) {
  if (!edit || (!duplicable(f) && !removable(f))) return null;
  return (
    <div className="ob-form-builder__field-tools">
      {duplicable(f) && <Button variant="ghost" size="sm" icon="copy" disabled={full} title={full ? `A form holds at most ${FORM_LIMITS.fields} items` : undefined} onClick={onDuplicate}>Duplicate</Button>}
      {removable(f) && <Button variant="ghost" size="sm" icon="trash" className="ob-form-builder__delete" onClick={onDelete}>Delete</Button>}
    </div>
  );
}

/** the Required switch with the key in small type beside it (the design's row), the key's problem or the published form's warning below */
function RequiredAndKey({ field: f, edit, checks, edits }: { field: DraftInput; edit: boolean; checks: FieldChecks; edits: PanelEdits }) {
  const id = useId();
  return (
    <div className="ob-form-builder__setting">
      <div className="ob-form-builder__required-row">
        {f.type !== "hidden" ? <label className="ob-form-builder__toggle"><Switch label="Required" checked={f.required} disabled={!edit || f.mapping === "email"} onChange={(on) => edits.update({ required: on })} />Required</label> : <span />}
        <label className="ob-form-builder__key" htmlFor={id}><span className="ob-form-builder__key-label">Key</span>
          <input id={id} className="ob-form-builder__key-input" aria-label="Key" value={f.key} maxLength={FORM_LIMITS.label} disabled={!edit} spellCheck={false} aria-invalid={!!checks.keyProblem || undefined} onChange={(e) => edits.update({ key: e.target.value })} /></label>
      </div>
      {checks.keyProblem && <p className="ob-form-builder__problem">{checks.keyProblem}</p>}
      {checks.keyWas && !checks.keyProblem && <p className="ob-form-builder__warning" role="status">This form is published: links and pages that fill in “{checks.keyWas}” stop filling this field once you save.</p>}
    </div>
  );
}

/** "Connects to": None and the details the field's type can hold, a detail another field stands for disabled (the address too, on a second
 *  email field); the address field's own connection cannot change (every form keeps it); no control where the type holds none */
function ConnectsTo({ field: f, edit, canConnect, edits }: { field: DraftInput; edit: boolean; canConnect: (m: FormMapping) => boolean; edits: PanelEdits }) {
  const offer = connectionsFor(f);
  if (!offer.length) return null;
  return (
    <Setting label="Connects to">{(id) => (
      <select id={id} className="ob-select" value={f.mapping ?? ""} disabled={!edit || f.mapping === "email"} onChange={(e) => edits.connect((e.target.value || null) as FormMapping | null)}>
        <option value="">None</option>
        {offer.map((m) => <option key={m} value={m} disabled={m !== f.mapping && !canConnect(m)}>{FORM_MAPPING_LABELS[m]}</option>)}
      </select>
    )}</Setting>
  );
}

/** one setting: its label over the control, a quiet line under it (a problem in the alert colour) */
function Setting({ label, hint, problem, children }: { label: string; hint?: string; problem?: boolean; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="ob-form-builder__setting">
      <label className="ob-field-label" htmlFor={id}>{label}</label>
      {children(id)}
      {hint && <p className={problem ? "ob-form-builder__problem" : "ob-form-builder__hint"}>{hint}</p>}
    </div>
  );
}

/** a select's options, each a label (its value follows the label), with Remove; Add an option */
function Options({ field: f, edit, edits }: { field: DraftInput; edit: boolean; edits: PanelEdits }) {
  const options = f.options ?? [];
  return (
    <div className="ob-form-builder__setting" role="group" aria-label="Options">
      <span className="ob-field-label">Options</span>
      {options.map((o, i) => (
        <div key={i} className="ob-form-builder__option">
          <Input className="ob-input--text" aria-label={`Option ${i + 1}`} value={o.label} maxLength={FORM_LIMITS.optionLabel} disabled={!edit} onChange={(e) => edits.optionLabel(i, e.target.value)} />
          {edit && <Button variant="icon" size="sm" icon="x" aria-label={`Remove option ${i + 1}`} disabled={options.length <= 1} onClick={() => edits.removeOption(i)} />}
        </div>
      ))}
      {edit && <Button variant="ghost" size="sm" icon="plus" disabled={options.length >= FORM_LIMITS.options} onClick={edits.addOption}>Add an option</Button>}
    </div>
  );
}

/** the project field's offer: a tick per open project, the name the person sees editable once ticked (a project added later is ticked by hand) */
function Projects({ field: f, edit, projects, edits }: { field: DraftInput; edit: boolean; projects: readonly OpenProject[]; edits: PanelEdits }) {
  const options = f.options ?? [];
  const stale = options.filter((o) => !projects.some((p) => p.id === o.value)); // archived since: shown so it can be taken out
  return (
    <fieldset className="ob-form-builder__setting ob-form-builder__projects" aria-label="Only these projects">
      <legend className="ob-field-label">Only these projects</legend>
      {projects.map((p) => {
        const at = options.findIndex((o) => o.value === p.id);
        return (
          <div key={p.id} className="ob-form-builder__project">
            <label className="ob-form-builder__project-pick"><input type="checkbox" aria-label={p.name} checked={at >= 0} disabled={!edit} onChange={(e) => edits.toggleProject(p, e.target.checked)} />{p.name}</label>
            {at >= 0 && <Input className="ob-input--text" aria-label={`Name shown for ${p.name}`} value={options[at]!.label} maxLength={FORM_LIMITS.optionLabel} disabled={!edit} onChange={(e) => edits.optionLabel(at, e.target.value)} />}
          </div>
        );
      })}
      {stale.map((o) => <p key={o.value} className="ob-form-builder__hint">{o.label}: no longer an open project{edit && <Button variant="ghost" size="sm" onClick={() => edits.toggleProject({ id: o.value, name: o.label }, false)}>Take it out</Button>}</p>)}
    </fieldset>
  );
}
